import { GroupHeader } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { GroupMenuItem } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader.types';
import { Flex, Text } from '@mantine/core';
import { GroupStatusDisplay } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerGroupHeader.types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	groupName: string;
	groupType: DocumentSigningRoleType;
	signerCount: number;
	requiredNumberOfSignatures?: number | null;
	status?: GroupStatusDisplay;
	onAssignClick?: () => void;
	onUnassignClick?: () => void;
	witnessCurrentlyRequired?: boolean;
	onToggleWitnessRequired?: () => void;
	simpleSignCurrentlyAllowed?: boolean;
	onToggleSimpleSignAllowed?: () => void;
}

const STATUS_CONFIG = {
	complete: {
		color: defaultTheme.v3.colors.teal[8],
	},
	outstanding: {
		color: defaultTheme.v3.colors.black[0],
	},
} as const;

export const SignerGroupHeader = ({
	groupName,
	groupType,
	signerCount,
	requiredNumberOfSignatures,
	status,
	onAssignClick,
	onUnassignClick,
	witnessCurrentlyRequired,
	onToggleWitnessRequired,
	simpleSignCurrentlyAllowed,
	onToggleSimpleSignAllowed,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const countDisplay =
		groupType === DocumentSigningRoleType.COUNTERPARTY && requiredNumberOfSignatures != null
			? `${requiredNumberOfSignatures}`
			: `${signerCount}`;

	const menuItems: GroupMenuItem[] = [];

	if (groupType === DocumentSigningRoleType.COUNTERPARTY) {
		if (onAssignClick) {
			menuItems.push({
				key: 'assign',
				label: tMipManage('documents_page.signer.menu.assign'),
				onClick: onAssignClick,
				testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_GROUP_MENU_ASSIGN,
			});
		}
		if (onUnassignClick) {
			menuItems.push({
				key: 'unassign',
				label: tMipManage('documents_page.signer.menu.unassign'),
				onClick: onUnassignClick,
			});
		}
	}

	// Simple-sign before witness — must match the per-signer card menu order built by
	// buildSignerMenuItems so the two toggles render consistently across the header and cards.
	if (onToggleSimpleSignAllowed) {
		menuItems.push({
			key: 'toggle-simple-sign',
			label: simpleSignCurrentlyAllowed
				? tMipManage('documents_page.signer.menu.disable_simple_sign')
				: tMipManage('documents_page.signer.menu.enable_simple_sign'),
			onClick: onToggleSimpleSignAllowed,
		});
	}

	if (onToggleWitnessRequired) {
		menuItems.push({
			key: 'toggle-witness-requirement',
			label: witnessCurrentlyRequired
				? tMipManage('documents_page.signer.menu.disable_witness_requirement')
				: tMipManage('documents_page.signer.menu.enable_witness_requirement'),
			onClick: onToggleWitnessRequired,
		});
	}

	return (
		<GroupHeader
			name={groupName}
			count={countDisplay}
			menuItems={menuItems}
			menuTestId={TEST_ELEMENT_IDS.DOCUMENT_SIGNER_GROUP_MENU_TRIGGER}
			countTone={status?.tone}
			rightContent={
				status ? (
					<Flex align="center" gap="0.25rem">
						<Text size="0.75rem" fw={500} c={STATUS_CONFIG[status.tone].color}>
							{status.label}
						</Text>
					</Flex>
				) : undefined
			}
		/>
	);
};
